import { useEffect, useRef, useState } from "react";
import type { SharePhase, ShareFlow } from "./use-share-flow.types";

class ShareFlowAborted extends Error {
  override name = "ShareFlowAborted";
}

const TIMINGS = {
  sending: 900,
  closing: 1400,
  exiting: 1650,
  ceremony: 1700,
  verified: 2650,
} as const;

export function useShareFlow(): ShareFlow {
  const [phase, setPhase] = useState<SharePhase>("entry");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const phaseRef = useRef<SharePhase>("entry");
  const timersRef = useRef<number[]>([]);
  const resolveVerifiedRef = useRef<(() => void) | null>(null);
  const rejectVerifiedRef = useRef<((reason: ShareFlowAborted) => void) | null>(null);

  function clearTimers() {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  }

  useEffect(() => clearTimers, []);

  function queue(fn: () => void, delay: number) {
    const t = window.setTimeout(fn, delay);
    timersRef.current.push(t);
  }

  function setSharePhase(next: SharePhase) {
    phaseRef.current = next;
    setPhase(next);
  }

  function start() {
    clearTimers();
    setErrorMessage(null);
    resolveVerifiedRef.current = null;
    rejectVerifiedRef.current = null;
    setSharePhase("encrypting");
    queue(() => setSharePhase("sending"), TIMINGS.sending);
    queue(() => setSharePhase("closing"), TIMINGS.closing);
    queue(() => setSharePhase("exiting"), TIMINGS.exiting);
    queue(() => setSharePhase("ceremony"), TIMINGS.ceremony);
    queue(() => {
      setSharePhase("verified");
      resolveVerifiedRef.current?.();
      resolveVerifiedRef.current = null;
      rejectVerifiedRef.current = null;
    }, TIMINGS.verified);
  }

  function abort(message: string) {
    clearTimers();
    setErrorMessage(message);
    setSharePhase("aborted");
    rejectVerifiedRef.current?.(new ShareFlowAborted());
    resolveVerifiedRef.current = null;
    rejectVerifiedRef.current = null;
  }

  function reset() {
    clearTimers();
    setErrorMessage(null);
    resolveVerifiedRef.current = null;
    rejectVerifiedRef.current = null;
    setSharePhase("idle");
  }

  function finishEntry() {
    if (phaseRef.current === "entry") {
      setSharePhase("idle");
    }
  }

  function whenVerified(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (phaseRef.current === "aborted") {
        reject(new ShareFlowAborted());
        return;
      }
      if (phaseRef.current === "verified") {
        resolve();
        return;
      }
      resolveVerifiedRef.current = resolve;
      rejectVerifiedRef.current = reject;
    });
  }

  function isAborted() {
    return phaseRef.current === "aborted";
  }

  const animating =
    phase === "encrypting" ||
    phase === "sending" ||
    phase === "closing" ||
    phase === "exiting" ||
    phase === "ceremony" ||
    phase === "verified";

  return {
    phase,
    errorMessage,
    encrypting: animating,
    cardSent:
      phase === "sending" ||
      phase === "closing" ||
      phase === "exiting" ||
      phase === "ceremony" ||
      phase === "verified",
    isAborted,
    whenVerified,
    start,
    abort,
    reset,
    finishEntry,
  };
}
